import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';
import { UUID_REGEX } from '../utils/slug';

type Run = {
  id: string;
  club_id: string;
  day_of_week: string;
  start_time: string | null;
};

type RsvpProfile = {
  display_name: string | null;
  username: string | null;
  profile_image: string | null;
};

type RsvpRow = {
  id: string;
  run_id: string;
  user_id: string;
  run_date: string;
  profile: RsvpProfile | null;
};


type AttendanceRow = {
  run_id: string;
  user_id: string;
  run_date: string;
};

function getIsoToday() {
  return new Date().toISOString().slice(0, 10);
}

function formatTime(time?: string | null) {
  if (!time) return 'Time TBD';
  const trimmed = time.trim();
  if (/am|pm/i.test(trimmed)) return trimmed;
  const parts = trimmed.split(':');
  if (parts.length < 2) return trimmed;
  const hour = Number(parts[0]);
  const minute = parts[1];
  if (Number.isNaN(hour)) return trimmed;
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const normalizedHour = hour % 12 || 12;
  return `${normalizedHour}:${minute} ${suffix}`;
}

function formatDateLabel(isoDate: string) {
  const d = new Date(isoDate + 'T00:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

export default function ConfirmAttendance() {
  const { id: clubId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { user, leader, canManageClub, loading: authLoading } = useAuth();

  const paramRunId = searchParams.get('runId');
  const paramDate = searchParams.get('date');
  const targetDate = paramDate || getIsoToday();

  const [club, setClub] = useState<{ id: string; name: string; leader_id: string | null } | null>(null);
  const [runs, setRuns] = useState<Run[]>([]);
  const [rsvps, setRsvps] = useState<RsvpRow[]>([]);
  const [confirmed, setConfirmed] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!clubId) return;

    async function load() {
      setLoading(true);

      // Fetch club first (UUID or slug) to get the stable UUID for sub-queries
      const isUuid = UUID_REGEX.test(clubId!);
      const { data: clubData } = await (
        isUuid
          ? supabase.from('clubs').select('id, name, leader_id').eq('id', clubId).single()
          : supabase.from('clubs').select('id, name, leader_id').eq('slug', clubId).single()
      );

      setClub(clubData ?? null);

      const clubUuid = (clubData as { id: string } | null)?.id ?? clubId!;

      // Build run query using the stable UUID
      let runQuery = supabase
        .from('runs')
        .select('id, club_id, day_of_week, start_time')
        .eq('club_id', clubUuid);

      if (paramRunId) {
        runQuery = runQuery.eq('id', paramRunId);
      } else {
        const dayName = new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(
          new Date(targetDate + 'T00:00:00')
        );
        runQuery = runQuery.eq('day_of_week', dayName).eq('is_active', true);
      }

      const { data: runsData } = await runQuery;

      const fetchedRuns = (runsData || []) as Run[];
      setRuns(fetchedRuns);

      if (fetchedRuns.length === 0) {
        setLoading(false);
        return;
      }

      const runIds = fetchedRuns.map((r) => r.id);

      const [{ data: rsvpRaw }, { data: attendanceData }] = await Promise.all([
        supabase
          .from('run_rsvps')
          .select('id, run_id, user_id, run_date')
          .in('run_id', runIds)
          .eq('run_date', targetDate),
        supabase
          .from('run_attendances')
          .select('run_id, user_id, run_date')
          .in('run_id', runIds)
          .eq('run_date', targetDate),
      ]);

      const rsvpRows = (rsvpRaw || []) as Omit<RsvpRow, 'profile'>[];

      // Fetch profiles separately to avoid relying on a FK join
      const userIds = [...new Set(rsvpRows.map((r) => r.user_id))];
      let profileMap: Record<string, RsvpProfile> = {};
      if (userIds.length > 0) {
        const { data: profilesData } = await supabase
          .from('profiles')
          .select('id, display_name, username, profile_image')
          .in('id', userIds);
        if (profilesData) {
          profileMap = (profilesData as (RsvpProfile & { id: string })[]).reduce<
            Record<string, RsvpProfile>
          >((acc, p) => {
            acc[p.id] = p;
            return acc;
          }, {});
        }
      }

      const normalizedRsvps: RsvpRow[] = rsvpRows.map((r) => ({
        ...r,
        profile: profileMap[r.user_id] ?? null,
      }));
      setRsvps(normalizedRsvps);

      const alreadyConfirmed = new Set<string>(
        ((attendanceData || []) as AttendanceRow[]).map(
          (row) => `${row.run_id}:${row.user_id}`
        )
      );
      setConfirmed(alreadyConfirmed);
      setLoading(false);
    }

    load();
  }, [clubId, paramRunId, targetDate]);

  const canManage = useMemo(() => {
    if (!club || !leader) return false;
    return canManageClub(club.id);
  }, [club, leader, canManageClub]);

  if (authLoading || loading) {
    return (
      <main className="min-h-screen bg-gradient-to-br from-[#fffdf3] via-[#fcfcfb] to-[#faf2fd] pb-16">
        <div className="mx-auto max-w-3xl px-4 pt-10 sm:px-6">
          <div className="text-sm text-black/60">Loading...</div>
        </div>
      </main>
    );
  }

  if (!user || !canManage) {
    return <Navigate to={`/clubs/${clubId}`} replace />;
  }

  const toggleConfirmed = (runId: string, userId: string) => {
    const key = `${runId}:${userId}`;
    setConfirmed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
    setSaved(false);
  };

  const handleSave = async () => {
    if (!leader) return;
    setSaving(true);

    const now = new Date().toISOString();
    const runIds = runs.map((r) => r.id);

    await supabase
      .from('run_attendances')
      .delete()
      .in('run_id', runIds)
      .eq('run_date', targetDate);

    const toInsert = rsvps
      .filter((rsvp) => confirmed.has(`${rsvp.run_id}:${rsvp.user_id}`))
      .map((rsvp) => ({
        run_id: rsvp.run_id,
        user_id: rsvp.user_id,
        club_id: clubId,
        run_date: targetDate,
        confirmed_by_leader_id: leader.id,
        confirmed_at: now,
      }));

    if (toInsert.length > 0) {
      await supabase.from('run_attendances').insert(toInsert);
    }

    setSaving(false);
    setSaved(true);
  };

  const rsvpsByRun = runs.reduce<Record<string, RsvpRow[]>>((acc, run) => {
    acc[run.id] = rsvps.filter((r) => r.run_id === run.id);
    return acc;
  }, {});

  return (
    <main className="min-h-screen bg-gradient-to-br from-[#fffdf3] via-[#fcfcfb] to-[#faf2fd] pb-16">
      <div className="mx-auto max-w-3xl px-4 pt-6 sm:px-6">
        <div className="space-y-6">
          <div className="overflow-hidden rounded-[2rem] border border-black/10 bg-white shadow-[0_18px_60px_rgba(0,0,0,0.08)]">
            <div className="bg-gradient-to-r from-[#fff7cc] via-white to-[#f6e8fb] px-8 py-10 md:px-10 md:py-12">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-black/45">
                {club?.name}
              </p>
              <h1 className="mt-3 text-4xl font-semibold tracking-tight text-black md:text-5xl">
                Confirm Attendance
              </h1>
              <p className="mt-2 text-sm text-black/55">
                {formatDateLabel(targetDate)}
              </p>
              <p className="mt-3 text-sm leading-6 text-black/65">
                Check off runners who showed up. This updates the attendees leaderboard.
              </p>
            </div>
          </div>

          {runs.length === 0 ? (
            <div className="rounded-3xl border border-black/10 bg-gradient-to-r from-[#fff7cc] via-white to-[#f6e8fb] p-8 shadow-sm">
              <h2 className="text-lg font-semibold text-black">No runs found</h2>
              <p className="mt-2 text-sm text-black/60">
                No runs are scheduled for this date.
              </p>
              <div className="mt-5">
                <Link
                  to={`/clubs/${clubId}`}
                  className="inline-flex rounded-2xl border border-black/10 bg-white px-4 py-2.5 text-sm font-medium text-black transition hover:bg-black hover:text-white"
                >
                  Back to Club
                </Link>
              </div>
            </div>
          ) : (
            <>
              {runs.map((run) => {
                const runRsvps = rsvpsByRun[run.id] || [];

                return (
                  <section
                    key={run.id}
                    className="rounded-[2rem] border border-black/10 bg-gradient-to-r from-[#fffdf2] via-white to-[#fbf4fe] p-6 shadow-sm md:p-8"
                  >
                    <h2 className="text-xl font-semibold tracking-tight text-black">
                      {run.day_of_week} — {formatTime(run.start_time)}
                    </h2>
                    <p className="mt-1 text-sm text-black/55">
                      {runRsvps.length} RSVP{runRsvps.length !== 1 ? 's' : ''}
                    </p>

                    {runRsvps.length === 0 ? (
                      <p className="mt-4 text-sm text-black/45">No RSVPs for this run.</p>
                    ) : (
                      <div className="mt-4 space-y-3">
                        {runRsvps.map((rsvp) => {
                          const key = `${rsvp.run_id}:${rsvp.user_id}`;
                          const isConfirmed = confirmed.has(key);
                          const name =
                            rsvp.profile?.display_name ||
                            rsvp.profile?.username ||
                            'Member';

                          return (
                            <label
                              key={rsvp.id}
                              className={`flex cursor-pointer items-center gap-4 rounded-2xl border p-4 transition ${
                                isConfirmed
                                  ? 'border-black/20 bg-white shadow-sm'
                                  : 'border-black/8 bg-white/60'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isConfirmed}
                                onChange={() => toggleConfirmed(rsvp.run_id, rsvp.user_id)}
                                className="h-5 w-5 rounded accent-black"
                              />

                              {rsvp.profile?.profile_image ? (
                                <img
                                  src={rsvp.profile.profile_image}
                                  alt={name}
                                  className="h-10 w-10 rounded-full object-cover"
                                />
                              ) : (
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#ffde59] text-sm font-bold text-black">
                                  {name.charAt(0).toUpperCase()}
                                </div>
                              )}

                              <span className="text-sm font-medium text-black">{name}</span>

                              {isConfirmed && (
                                <span className="ml-auto text-xs font-semibold text-black/50">
                                  Confirmed
                                </span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </section>
                );
              })}

              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex rounded-2xl bg-black px-6 py-3 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save Attendance'}
                </button>

                {saved && (
                  <span className="text-sm font-medium text-black/60">Attendance saved ✓</span>
                )}

                <Link
                  to={`/clubs/${clubId}`}
                  className="ml-auto text-sm text-black/45 transition hover:text-black"
                >
                  Back to Club
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </main>
  );
}
